import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Search, X } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { FlashBanner } from "@/components/FlashBanner"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { BulkActionBar } from "@/components/inbox/BulkActionBar"
import { ThreadWorkspace } from "@/components/inbox/ThreadWorkspace"
import { InboxFilterControls, InboxFilterPills } from "@/components/inbox/InboxFilterControls"
import { InboxRail } from "@/components/inbox/InboxRail"
import {
  classificationLabel,
  formatInboxDate,
  inboxFilterParams,
  laneLabel,
  statusLabel,
} from "@/components/inbox/helpers"
import type {
  InboxClient,
  InboxCounts,
  InboxFilters,
  InboxReply,
  SelectedReply,
} from "@/components/inbox/types"
import { useKeyboardShortcuts } from "@/components/inbox/useKeyboardShortcuts"
import { cn } from "@/lib/utils"

type InboxProps = {
  readonly replies: readonly InboxReply[]
  readonly clients: readonly InboxClient[]
  readonly classifications: readonly string[]
  readonly counts: InboxCounts
  readonly filters: InboxFilters
  readonly selected_reply: SelectedReply | null
}

type ActionCableSubscription = {
  readonly unsubscribe: () => void
}

type ActionCableConsumer = {
  readonly subscriptions: {
    readonly create: (
      channel: { readonly channel: "InboxChannel" },
      callbacks: { readonly received: () => void },
    ) => ActionCableSubscription
  }
}

type ActionCableGlobal = {
  readonly createConsumer: () => ActionCableConsumer
}

declare global {
  interface Window {
    readonly ActionCable?: ActionCableGlobal
  }
}

export default function Inbox({
  replies,
  clients,
  classifications,
  counts,
  filters,
  selected_reply,
}: InboxProps) {
  const description =
    "Review SmartLead replies by folder, client, classification, and full-text search in ReplyOps."
  const [query, setQuery] = React.useState(filters.q)
  const [selectedIds, setSelectedIds] = React.useState<readonly number[]>([])
  const searchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const reloadInbox = () => {
      router.reload({ only: ["replies", "counts", "selected_reply"] })
    }

    const consumer = window.ActionCable?.createConsumer()
    if (consumer) {
      const subscription = consumer.subscriptions.create(
        { channel: "InboxChannel" },
        { received: reloadInbox },
      )
      return () => subscription.unsubscribe()
    }

    const timer = window.setInterval(reloadInbox, 10_000)
    return () => window.clearInterval(timer)
  }, [])

  // Drop bulk selections that are no longer visible after a filter/search/reload,
  // so bulk actions can never target replies that left the list.
  React.useEffect(() => {
    setSelectedIds((current) => {
      const visible = current.filter((id) => replies.some((reply) => reply.id === id))
      return visible.length === current.length ? current : visible
    })
  }, [replies])

  const selectedIndex = replies.findIndex((reply) => reply.id === selected_reply?.id)
  const selectReply = React.useCallback(
    (id: number) => {
      router.get(
        "/inbox",
        { ...inboxFilterParams(filters), selected: id },
        { only: ["selected_reply"], preserveState: true, preserveScroll: true },
      )
    },
    [filters],
  )

  const selectOffset = React.useCallback(
    (offset: number) => {
      const nextIndex = selectedIndex < 0 ? 0 : selectedIndex + offset
      const next = replies[Math.max(0, Math.min(replies.length - 1, nextIndex))]
      if (next) selectReply(next.id)
    },
    [replies, selectReply, selectedIndex],
  )

  const archiveSelected = React.useCallback(() => {
    if (selected_reply) router.post(`/replies/${selected_reply.id}/archive`, {}, { preserveScroll: true })
  }, [selected_reply])

  const snoozeSelected = React.useCallback(() => {
    if (selected_reply) router.post(`/replies/${selected_reply.id}/snooze`, { hours: 24 }, { preserveScroll: true })
  }, [selected_reply])

  const regenerateSelected = React.useCallback(() => {
    if (selected_reply) router.post(`/replies/${selected_reply.id}/regenerate`, {}, { preserveScroll: true })
  }, [selected_reply])

  const sendSelected = React.useCallback(() => {
    if (selected_reply) {
      document.querySelector<HTMLButtonElement>("[data-send-button]")?.click()
    }
  }, [selected_reply])

  useKeyboardShortcuts(
    React.useMemo(
      () => ({
        onNext: () => selectOffset(1),
        onPrevious: () => selectOffset(-1),
        onArchive: archiveSelected,
        onSnooze: snoozeSelected,
        onRegenerate: regenerateSelected,
        onSend: sendSelected,
        onFocusSearch: () => searchRef.current?.focus(),
      }),
      [archiveSelected, regenerateSelected, selectOffset, sendSelected, snoozeSelected],
    ),
  )

  const submitSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      router.get(
        "/inbox",
        { ...inboxFilterParams(filters), q: query || undefined },
        { preserveState: true, preserveScroll: true },
      )
    }
    if (e.key === "Escape") clearSearch()
  }

  const clearSearch = () => {
    setQuery("")
    if (filters.q) {
      router.get(
        "/inbox",
        { ...inboxFilterParams(filters), q: undefined },
        { preserveState: true, preserveScroll: true },
      )
    }
  }

  const toggleSelectedId = (replyId: number) => {
    setSelectedIds((current) =>
      current.includes(replyId)
        ? current.filter((id) => id !== replyId)
        : [...current, replyId],
    )
  }

  const clearSelected = () => {
    router.get(
      "/inbox",
      inboxFilterParams(filters),
      { preserveState: true, preserveScroll: true },
    )
  }

  return (
    <>
      <Head title="Inbox">
        <meta name="description" content={description} />
        <meta property="og:title" content="Inbox" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell fullHeight nav={<InboxRail counts={counts} filters={filters} />}>
        <FlashBanner />

        <div className="grid h-full min-h-0 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(300px,340px)_minmax(0,1fr)]">
          {/* Region 1: Message list (narrow column) */}
          <div
            className={cn(
              "flex min-h-0 flex-col border-r border-hairline",
              selected_reply ? "hidden lg:flex" : "flex",
            )}
          >
            {/* Toolbar: global search + filter pills + funnel */}
            <div className="relative flex shrink-0 flex-wrap items-center gap-1.5 border-b border-hairline py-2 pl-3 pr-14 lg:pr-3">
              <div className="relative min-w-[140px] flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
                <Input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={submitSearch}
                  placeholder="Search all mail..."
                  aria-label="Search all mail"
                  className="h-8 pl-8 pr-7 text-[13px]"
                />
                {query && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-ink-muted hover:text-ink-display"
                    aria-label="Clear search"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              <InboxFilterPills filters={filters} clients={clients} />
              <InboxFilterControls
                filters={filters}
                clients={clients}
                classifications={classifications}
                counts={counts}
              />
            </div>

            {/* Bulk action bar */}
            <BulkActionBar selectedIds={selectedIds} onClear={() => setSelectedIds([])} />

            {/* Reply list (scroller) */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
              <ReplyList
                replies={replies}
                selectedReplyId={selected_reply?.id ?? null}
                selectedIds={selectedIds}
                onSelect={selectReply}
                onToggle={toggleSelectedId}
              />
            </div>
          </div>

          {/* Region 2: Reading pane (widest column) */}
          <div
            className={cn(
              "flex min-h-0 flex-col",
              !selected_reply ? "hidden lg:flex" : "flex",
            )}
          >
            {selected_reply ? (
              <>
                {/* Mobile back button */}
                <div className="flex h-10 shrink-0 items-center border-b border-hairline px-3 lg:hidden">
                  <button
                    type="button"
                    onClick={clearSelected}
                    className="text-[13px] text-accent-display hover:underline"
                  >
                    ← Back to replies
                  </button>
                </div>

                {/* Reading pane scroller */}
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
                  <ThreadWorkspace reply={selected_reply} clients={clients} classifications={classifications} />
                </div>
              </>
            ) : (
              <section className="flex min-h-0 flex-1 items-center justify-center p-8 text-center">
                <div>
                  <h2>No thread selected</h2>
                  <p className="mx-auto mt-2 max-w-sm">
                    Choose a reply on the left to review the conversation, edit the draft, and preview a send.
                  </p>
                </div>
              </section>
            )}
          </div>
        </div>
      </AppShell>
    </>
  )
}

function ReplyList({
  replies,
  selectedReplyId,
  selectedIds,
  onSelect,
  onToggle,
}: {
  readonly replies: readonly InboxReply[]
  readonly selectedReplyId: number | null
  readonly selectedIds: readonly number[]
  readonly onSelect: (replyId: number) => void
  readonly onToggle: (replyId: number) => void
}) {
  if (replies.length === 0) {
    return (
      <div className="flex min-h-[300px] items-center justify-center px-4 py-12 text-center">
        <div>
          <h2>No replies match these filters</h2>
          <p className="mx-auto mt-2 max-w-md text-[13px]">
            New inbound replies will appear here after the ingest pipeline classifies them for this agency.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="divide-y divide-hairline">
      {replies.map((reply) => (
        <article
          key={reply.id}
          className={cn(
            "grid cursor-pointer grid-cols-[auto_1fr] gap-2.5 px-3 py-2.5",
            selectedReplyId === reply.id ? "bg-accent-faded" : "bg-page hover:bg-surface",
          )}
          onClick={() => onSelect(reply.id)}
        >
          <Checkbox
            checked={selectedIds.includes(reply.id)}
            onChange={() => onToggle(reply.id)}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${reply.lead_name}`}
            className="mt-0.5"
          />
          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[13px] font-semibold text-ink-display">
                {reply.lead_name}
                {reply.company ? ` — ${reply.company}` : ""}
              </span>
              <time className="shrink-0 text-[11px] text-ink-muted">
                {formatInboxDate(reply.received_at)}
              </time>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {reply.client && (
                <span className="flex min-w-0 items-center gap-1">
                  <span
                    style={{ backgroundColor: reply.client.color_hex }}
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                  />
                  <span className="truncate text-[12px] text-ink-muted">{reply.client.display_name}</span>
                </span>
              )}
              {reply.op_lane && (
                <Badge tone="muted" className="text-[11px]">
                  {laneLabel(reply.op_lane)}
                </Badge>
              )}
              <ClassificationBadge classification={reply.classification} confidence={reply.confidence} />
              {reply.needs_reply ? (
                <Badge tone="signal" className="text-[11px]">Awaiting reply</Badge>
              ) : (
                <Badge tone={reply.status === "sent" ? "signal" : "muted"} className="text-[11px]">
                  {statusLabel(reply.status)}
                </Badge>
              )}
            </div>
            <p className="mt-1 truncate text-[12px] text-ink-muted">
              {reply.snippet || "No reply body"}
            </p>
          </div>
        </article>
      ))}
    </div>
  )
}

function ClassificationBadge({
  classification,
  confidence,
}: {
  readonly classification: string
  readonly confidence: number
}) {
  const positive = new Set([
    "interested",
    "soft_interested",
    "question",
    "case_study_request",
    "pricing_question",
  ])
  const muted = new Set(["referral", "ooo", "bounce", "no_longer_employed", "other", "unclassified"])
  const tone = positive.has(classification)
    ? "accent"
    : muted.has(classification)
      ? "muted"
      : "neutral"

  return (
    <Badge tone={tone} className="text-[11px]">
      {classificationLabel(classification)}
      {confidence > 0 && <span className="ml-1 opacity-75">{confidence}%</span>}
    </Badge>
  )
}
